import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';

import useFundContext from '@/contexts/Fund/useFundContext';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import Debit, { DEBIT_METHOD_BANK, DEBIT_METHOD_CREDIT } from '@/models/Debit';
import { aptDebitToStandingOrder, isAptEndingSoon, isAptOrder } from './aptStandingOrders';
import DataTable from '@/components/ui/DataTable/DataTable';
import useTableDensity from '@/hooks/useTableDensity';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import useBoardTabs from '@/components/ui/BoardTabs/useBoardTabs';
import { FabActionCard } from '@/components/ui/Fab/Fab';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import CustomMenu from '@/components/ui/Menu';
import AnnouncementAnchor from '@/components/Announcements/AnnouncementAnchor';
import eyeIcon from '@/assets/icons/eye.svg';
import hourglassIcon from '@/assets/icons/hourglass.svg';
import checkCircleIcon from '@/assets/icons/check-circle.svg';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import StandingOrderChargeDialog from './AdditionalInfo/StandingOrderChargeDialog';
import ToggleOrderDialog from './OrderActions/ToggleOrderDialog';
import CancelOrderDialog from './OrderActions/CancelOrderDialog';
import EditOrderDialog from './OrderActions/EditOrderDialog';
import EditBankOrderDialog from './OrderActions/EditBankOrderDialog';
import {
  AptOrderOverride,
  BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY,
  UpdateBankStandingOrderRequest,
  applyOverride,
  bankCollectionLocksUrl,
  canCancelBank,
  canEditBank,
  canFreezeBank,
  canReactivateBank,
  getBankOrderState,
  lockedCollection,
  toOrderState,
} from './bankStandingOrderActions';
import {
  OrderActionNotice,
  STANDING_ORDER_ACTIONS_FEATURE_KEY,
  StandingOrderActionKind,
  StandingOrderActionResult,
  UpdateCreditStandingOrderRequest,
  actionLogUrl,
  applyAction,
  canCancel,
  canEdit,
  canToggle,
  detailsUrl,
  withLiveTerms,
  getOrderState,
} from './standingOrderActions';
import NewStandingOrderDialog from './NewStandingOrder/NewStandingOrderDialog';
import { BANK_STANDING_ORDER_CREATE_FEATURE_KEY } from './NewBankStandingOrder/bankOrderCreation';
import FiltersArea from './filters/FiltersArea';
import { StandingOrdersCacheRefresh } from '@/models/StandingOrdersCacheRefresh';
import { CreditStandingOrderDetailsDtoIn } from '@/models/StandingOrderDetails';
import StandingOrdersSummary from './StandingOrdersSummary';
import StandingOrderCard from './StandingOrderCard';
import styles from './StandingOrdersPage.module.scss';
import {
  ACTIVE_COLORS,
  LAST_CHARGE_COLORS,
  buildExportColumns,
  cardNeedsAttention,
  countAttention,
  donorColumn,
  filterBoardRows,
  makeStandingOrderSearch,
  orderColumns,
  summarizeFailures,
} from './boardColumns';
import {
  AttentionFilter,
  formatDate,
  isInactive,
  getLastChargeStatus,
  getPaymentSourceLabel,
  STANDING_ORDER_TYPE_BANK,
  STANDING_ORDER_TYPE_CREDIT,
} from './standingOrderModel';

/**
 * Which board this is (HAT-1135). Both read the same columns, filters, summary, card and blade;
 * they differ in where the rows come from and in what a row can be asked to do.
 *   credit - Nedarim's credit-card orders, plus the few credit orders the office charges itself
 *            through the accounting system (tagged «דרך המשרד»; Druk, 23.9.2026).
 *   bank   - the accounting system's bank orders only. Nothing here can be charged or created
 *            from the site: the office does that against a signed mandate.
 */
export type StandingOrdersVariant = 'credit' | 'bank';

interface StandingOrdersPageProps {
  variant: StandingOrdersVariant;
}

/**
 * ההמתנה שאחרי לחיצה על רענון (HAT-1237).
 *
 * מאותה מכסה שבגללה אין כאן retry: עשרים קריאות בשעה למוסד, משותפות לכל
 * הקרנות שתחתיו. חצי דקה אינה מורגשת למי שבאמת רוצה לראות מה השתנה, ועוצרת
 * את הלחיצות החוזרות על מסך שלא חזר - המקרה שמכלה את המכסה לכולם.
 */

const StandingOrdersPage: React.FC<StandingOrdersPageProps> = ({ variant }) => {
  const isBankBoard = variant === 'bank';
  const { currentFund } = useFundContext();
  const { t, i18n } = useTranslation();
  const standingOrderSearch = useMemo(
    () => makeStandingOrderSearch(i18n.language, t('standingOrders.autoFrozen')),
    [i18n.language, t]
  );
  const hasInquiries = useInquiriesFeature();
  const queryClient = useQueryClient();
  /* HAT-1170: the narrow screen shows cards, the board tabs, and the primary action as a card. */
  const { isMobile } = useDeviceDetection();
  const { isVisible: hasBoardTabs } = useBoardTabs();
  const [searchQuery, setSearchQuery] = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [density, setDensity] = useTableDensity(isBankBoard ? 'table.density.bankStandingOrders' : 'table.density.standingOrders', 'normal');
  const [selectedOrder, setSelectedOrder] = useState<NedarimStandingOrderDtoIn>();
  const [creationOpen, setCreationOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  /*
   * The states a coordinator opens this board to find, each one click. They replace the
   * "last charge" and "activity" dropdowns, which offered every value of both. A card about
   * to expire was not filterable at all before, and it is the most common reason an order
   * starts failing. «תקין» and «מוקפא» came later (HAT-1447), at the office's request:
   * "succeeded" alone was dropped as an answer nobody asks for, but "which orders are fine" is
   * one, and a frozen order had no pill at all.
   */
  const [attention, setAttention] = useState<Set<AttentionFilter>>(new Set());
  const toggleAttention = useCallback((key: AttentionFilter) => {
    setAttention((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const [chargeTarget, setChargeTarget] = useState<NedarimStandingOrderDtoIn>();
  /*
   * HAT-1317: freeze, reactivate, edit and cancel. Mounted here rather than in the blade, like
   * the charge dialog, because the row menu opens them too. The notice is what the blade says
   * after an action, for the order it was taken on.
   */
  const hasOrderActions = useFeature(STANDING_ORDER_ACTIONS_FEATURE_KEY);
  /*
   * HAT-1441: the same actions on the bank board, written into the office's APT. Their own flag:
   * this is the first thing a coordinator does that writes to the office's accounting system.
   */
  const hasBankActions = useFeature(BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY) && isBankBoard;
  /*
   * HAT-1478: a new bank order, written into the office's APT. Its own flag, like the actions: the
   * office takes the mandate to the bank from there, and opens this when it is ready to.
   */
  const hasBankCreate = useFeature(BANK_STANDING_ORDER_CREATE_FEATURE_KEY) && isBankBoard;
  const canCreate = isBankBoard ? hasBankCreate : true;
  // Where the flag's announcement bubble points (SeedAnnouncement_BankStandingOrderCreate.sql); nothing on the credit board.
  const createAnchor = isBankBoard ? BANK_STANDING_ORDER_CREATE_FEATURE_KEY : '';
  // «מוקפאת» rather than «לא פעילה» wherever the board itself can freeze.
  const frozenTerm = hasOrderActions || hasBankActions;
  const [actionTarget, setActionTarget] = useState<{ kind: StandingOrderActionKind; order: NedarimStandingOrderDtoIn }>();
  const [actionNotice, setActionNotice] = useState<OrderActionNotice | null>(null);
  /*
   * What an action just wrote into APT, per order, until the Azure copy the list is read from has it
   * too (bankStandingOrderActions.applyOverride) - otherwise a re-read seconds after a freeze could
   * show the order running again.
   */
  const [aptOverrides, setAptOverrides] = useState<Record<number, AptOrderOverride>>({});
  /*
   * The collection dates the office has locked before sending its file to the bank: an order that
   * collects on one is not changed until the day has passed (the server refuses it as well).
   */
  const { data: collectionLocks } = useGetAuthApi<string[]>(
    hasBankActions && currentFund ? bankCollectionLocksUrl(currentFund.id) : '',
  );
  const lockOf = useCallback(
    (order: NedarimStandingOrderDtoIn) => (hasBankActions && isAptOrder(order) ? lockedCollection(order, collectionLocks) : null),
    [hasBankActions, collectionLocks],
  );



  const handleRowClick = useCallback((row: NedarimStandingOrderDtoIn) => {
    setSelectedOrder(row);
  }, []);

  const handleCloseBlade = useCallback(() => {
    setSelectedOrder(undefined);
  }, []);

  // The bank board never asks Nedarim: its orders are not there (the feed was switched off 10.9.2026).
  const apiUrl = currentFund && !isBankBoard ? `nedarim-standing-orders/${currentFund.id}` : '';
  const {
    data: nedarimOrders,
    isLoading: isNedarimLoading,
    isError: isNedarimError,
    refetch: refetchNedarim,
  } = useGetAuthApi<NedarimStandingOrderDtoIn[]>(apiUrl, undefined, {
    /*
     * No retry on this one. Nedarim cap GetKevaJson at twenty calls an hour per mosad, and
     * every fund without a mosad of its own reads the parent's list, so the budget is shared.
     * The global default of two retries turns one failure into three calls against it - and
     * the screen already offers the coordinator a "try again" for the case where retrying is
     * what they want.
     */
    retry: false,
    /*
     * ⚠️ ובאותה נשימה, בלי meta.refreshable: הלוח הזה אינו מצטרף לרענון של
     * האזור האישי (ר' useGlobalRefresh), כי כל לחיצה כזו הייתה שורפת קריאה
     * מאותה מכסה. במקומה יש כאן כפתור משלו, עם המתנה בין משיכות.
     */
  });

  /*
   * The server caches Nedarim's list for five minutes - twenty reads an hour is all they allow,
   * and the parent's list is shared by every fund without a mosad of its own. A refetch alone
   * came back with the same cached answer, so a standing order the coordinator had just created
   * took up to five minutes to appear. After a creation or a charge the board now asks the
   * server to drop its copy first, unconditionally: that is exactly when the list is known to
   * be behind, and those two actions are rare enough to be no threat to the budget.
   *
   * There is no refresh button any more. Inside the cache window it could show nothing newer
   * than the board already had, and outside it the board refetches on its own on every visit;
   * a button that mostly renews a timestamp promises more than it gives.
   */
  const { mutateAsync: dropServerCache } = usePostAuthApi<StandingOrdersCacheRefresh, { force: boolean }>(
    currentFund ? `nedarim-standing-orders/${currentFund.id}/invalidate-cache` : '',
  );

  /*
   * The accounting system's own list (HAT-1135): every bank order the office keeps, and the few
   * credit orders it charges itself rather than through Nedarim. The same endpoint the old site's
   * «הוראות קבע» page has always read; the bank board is built from it alone, the credit board
   * appends its credit rows after Nedarim's.
   */
  /*
   * Behind the bank screen's flag on the credit board too: until it is released, the credit board
   * is exactly what it was - Nedarim only, no call to the accounting system.
   */
  const hasAptOrders = useFeature('bank-standing-orders');
  const debitsUrl = currentFund && (isBankBoard || hasAptOrders)
    ? `funds/financial-data/${currentFund.id}/debits` : '';
  const {
    data: aptDebits,
    isLoading: isAptLoading,
    isError: isAptError,
    refetch: refetchApt,
  } = useGetAuthApi<Debit[]>(debitsUrl);

  const aptOrders = useMemo(() => {
    const method = isBankBoard ? DEBIT_METHOD_BANK : DEBIT_METHOD_CREDIT;
    return (aptDebits ?? [])
      .filter((debit) => debit.paymentMethod === method)
      .map((debit) => aptDebitToStandingOrder(applyOverride(debit, aptOverrides[debit.donationId])));
  }, [aptDebits, isBankBoard, aptOverrides]);

  /*
   * Everything the fund has, from whichever source this board reads. Undefined while the board's
   * own source has not answered, so the summary and the empty state keep telling loading apart
   * from empty. On the credit board the accounting rows are an addition: if that call fails the
   * Nedarim rows still show, and the office's few orders are simply absent until a retry.
   */
  const visibleStandingOrders = useMemo<NedarimStandingOrderDtoIn[] | undefined>(() => {
    if (isBankBoard) return aptDebits ? aptOrders : undefined;
    if (!nedarimOrders) return undefined;
    return aptOrders.length > 0 ? [...nedarimOrders, ...aptOrders] : nedarimOrders;
  }, [isBankBoard, aptDebits, aptOrders, nedarimOrders]);

  const isLoading = isBankBoard ? isAptLoading : isNedarimLoading;
  const isError = isBankBoard ? isAptError : isNedarimError;
  const refetch = isBankBoard ? refetchApt : refetchNedarim;

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const resourceId = searchParams.get('resourceId');
    if (resourceId && visibleStandingOrders) {
      const order = visibleStandingOrders.find(o => String(o.standingOrderId) === resourceId);
      if (order) {
        setSelectedOrder(order);
        searchParams.delete('resourceId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [visibleStandingOrders]);
  /*
   * The blade holds the row it was opened with. When the list is read again - after an action, a
   * charge, a creation - it is handed the same order as the list now has it, so it shows what the
   * board shows (and the board keeps the row marked as the open one, which compares by object).
   */
  useEffect(() => {
    if (!selectedOrder || !visibleStandingOrders) return;
    const fresh = visibleStandingOrders.find((o) =>
      o.standingOrderId === selectedOrder.standingOrderId && o.mosadSource === selectedOrder.mosadSource);
    if (fresh && fresh !== selectedOrder) setSelectedOrder(fresh);
  }, [visibleStandingOrders, selectedOrder]);

  // Without this the blade stayed open on the fund we just left, and asked the new fund's
  // endpoint for an id that belongs to the old one.
  useEffect(() => {
    setSelectedOrder(undefined);
    setAttention(new Set());
    setBannerDismissed(false);
    setSearchQuery('');
  }, [currentFund?.id]);

  const handleClearFilters = useCallback(() => {
    setDateRange(null);
    setAttention(new Set());
    setSearchQuery('');
  }, []);

  /*
   * After an action Nedarim accepted: the board and the blade show the order as it now is at
   * once, then the list is read again to confirm it. The server already dropped the cached list
   * of the order's mosad, so this re-read is the only one it takes - unlike a creation, which has
   * to drop every list because it happened in Nedarim's iframe, out of this API's sight.
   * A cancelled order leaves the board, and its blade closes.
   */
  const handleActionDone = useCallback((
    order: NedarimStandingOrderDtoIn,
    kind: StandingOrderActionKind,
    result: StandingOrderActionResult,
    request?: UpdateCreditStandingOrderRequest | UpdateBankStandingOrderRequest,
  ) => {
    if (!currentFund) return;
    const same = (o: NedarimStandingOrderDtoIn) =>
      o.standingOrderId === order.standingOrderId && o.mosadSource === order.mosadSource;

    /*
     * A bank order in APT (HAT-1441) stays on the board whatever was done to it - a cancelled one
     * reads «בוטלה». The row shows what the server wrote at once; the list is read again, and takes
     * over as soon as the Azure copy has caught up with the office's database.
     */
    if (isAptOrder(order)) {
      const written = result.order;
      if (written) {
        setAptOverrides((current) => ({ ...current, [order.standingOrderId]: { state: written, at: Date.now() } }));
      }
      setActionNotice(kind === 'cancel' ? null : { orderId: order.standingOrderId, kind, nextDate: result.nextDate });
      queryClient.invalidateQueries({ queryKey: [debitsUrl] });
      queryClient.invalidateQueries({ queryKey: [actionLogUrl(currentFund.id, order)] });
      return;
    }

    if (kind === 'cancel') {
      queryClient.setQueriesData<NedarimStandingOrderDtoIn[]>({ queryKey: [apiUrl] }, (rows) => rows?.filter((o) => !same(o)));
      setSelectedOrder((current) => (current && same(current) ? undefined : current));
      setActionNotice(null);
    } else {
      const updated = applyAction(order, kind, result, request as UpdateCreditStandingOrderRequest | undefined);
      queryClient.setQueriesData<NedarimStandingOrderDtoIn[]>({ queryKey: [apiUrl] }, (rows) => rows?.map((o) => (same(o) ? updated : o)));
      setSelectedOrder((current) => (current && same(current) ? updated : current));
      setActionNotice({ orderId: order.standingOrderId, kind, nextDate: result.nextDate });
    }

    queryClient.invalidateQueries({ queryKey: [apiUrl] });
    queryClient.invalidateQueries({ queryKey: [detailsUrl(currentFund.id, order)] });
    queryClient.invalidateQueries({ queryKey: [actionLogUrl(currentFund.id, order)] });
  }, [currentFund, apiUrl, debitsUrl, queryClient]);

  /** What the row itself offers: the charge again on a failure, otherwise an inquiry about it. */
  const renderRowOwnAction = (order: NedarimStandingOrderDtoIn) => {
    // Nothing the site can charge behind an accounting-system order (HAT-1135): the office does that.
    if (getLastChargeStatus(order) === 'failed' && !isAptOrder(order)) {
      return (
        <button
          type="button"
          className={styles.retryChargeButton}
          // the row itself opens the blade
          onClick={(e) => { e.stopPropagation(); setChargeTarget(order); }}
        >
          {t('standingOrders.chargeAgain')}
        </button>
      );
    }
    // The inquiry button exists only where the inquiries feature does.
    if (!hasInquiries) return null;
    return (
      <OpenInquiryButton
        resourceType="StandingOrder"
        resourceId={String(order.standingOrderId)}
      />
    );
  };

  /** The row menu: the same actions the blade offers, for a coordinator who knows what they want. */
  const rowMenuItems = (order: NedarimStandingOrderDtoIn) => {
    const items: { icon?: string; text: string; onClick: () => void; danger?: boolean; disabled?: boolean; hint?: string }[] = [
      { icon: eyeIcon, text: t('standingOrders.orderActions.details'), onClick: () => setSelectedOrder(order) },
    ];

    // A bank order in APT (HAT-1441): an order the office stopped offers its details only.
    if (isAptOrder(order)) {
      const bankState = getBankOrderState(order);
      // While the office sends the order's collection to the bank, its actions stay in view, held, with why.
      const locked = lockOf(order);
      const held = locked
        ? { disabled: true, hint: t('standingOrders.orderActions.bank.locked.hint', { date: formatDate(locked, i18n.language) }) }
        : {};
      if (canFreezeBank(bankState) || canReactivateBank(bankState, order)) {
        const kind = canReactivateBank(bankState, order) ? 'reactivate' : 'freeze';
        items.push({
          icon: kind === 'reactivate' ? checkCircleIcon : hourglassIcon,
          text: t(`standingOrders.orderActions.${kind}`),
          onClick: () => setActionTarget({ kind, order }),
          ...held,
        });
      }
      if (canEditBank(bankState)) {
        items.push({ icon: editIcon, text: t('standingOrders.orderActions.edit'), onClick: () => setActionTarget({ kind: 'edit', order }), ...held });
      }
      if (canCancelBank(bankState)) {
        items.push({
          icon: deleteIcon,
          text: t('standingOrders.orderActions.cancel'),
          onClick: () => setActionTarget({ kind: 'cancel', order }),
          danger: !locked,
          ...held,
        });
      }
      return items;
    }

    const state = getOrderState(order);
    if (canToggle(state)) {
      const kind = state === 'disabled' ? 'reactivate' : 'freeze';
      items.push({
        icon: kind === 'reactivate' ? checkCircleIcon : hourglassIcon,
        text: t(`standingOrders.orderActions.${kind}`),
        onClick: () => setActionTarget({ kind, order }),
      });
    }
    if (canEdit(state)) {
      items.push({ icon: editIcon, text: t('standingOrders.orderActions.edit'), onClick: () => setActionTarget({ kind: 'edit', order }) });
    }
    if (canCancel(state)) {
      items.push({
        icon: deleteIcon,
        text: t('standingOrders.orderActions.cancel'),
        onClick: () => setActionTarget({ kind: 'cancel', order }),
        danger: true,
      });
    }
    return items;
  };

  /*
   * The board's own refresh (HAT-1382). The refresh control in the fund bar does not reach this
   * list - it is left out of that refresh on purpose, for the quota - so a coordinator who
   * changed an order in Nedarim's own interface and pressed it saw a new time and the old amount.
   * This one asks the server to drop its copy, and says so when the server kept a list it had
   * read moments ago, instead of looking as if it had refreshed.
   */
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [isRefreshingList, setIsRefreshingList] = useState(false);
  /*
   * An order opened in the blade is read live from Nedarim, and the list it was opened from can be
   * five minutes behind. When the live read differs, the row takes its amount, next date and
   * payments left, so the table and the blade stop disagreeing (HAT-1382). Only after the read has
   * settled: details kept from an earlier opening must not overwrite a row before the fresh ones.
   */
  const liveDetailsUrl = currentFund && selectedOrder && selectedOrder.type === STANDING_ORDER_TYPE_CREDIT && !isAptOrder(selectedOrder)
    ? detailsUrl(currentFund.id, selectedOrder) : '';
  const { data: liveDetails, isFetching: isLiveFetching } = useGetAuthApi<CreditStandingOrderDetailsDtoIn>(liveDetailsUrl);
  useEffect(() => {
    if (!selectedOrder || !liveDetails || isLiveFetching || !apiUrl) return;
    const live = withLiveTerms(selectedOrder, liveDetails);
    if (live.amount === selectedOrder.amount && live.nextDate === selectedOrder.nextDate
      && live.remainingPayments === selectedOrder.remainingPayments) return;

    const same = (o: NedarimStandingOrderDtoIn) =>
      o.standingOrderId === selectedOrder.standingOrderId && o.mosadSource === selectedOrder.mosadSource;
    queryClient.setQueriesData<NedarimStandingOrderDtoIn[]>({ queryKey: [apiUrl] }, (rows) => rows?.map((o) => (same(o) ? live : o)));
    setSelectedOrder(live);
  }, [selectedOrder, liveDetails, isLiveFetching, apiUrl, queryClient]);

  const handleManualRefresh = useCallback(() => {
    setIsRefreshingList(true);
    setRefreshNote(null);
    dropServerCache({ force: false })
      .then((refresh) => {
        if (refresh && !refresh.refreshed) {
          setRefreshNote(t('standingOrders.refreshKept', { seconds: refresh.retryAfterSeconds }));
        }
      })
      .catch(() => setRefreshNote(t('standingOrders.refreshFailed')))
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: [apiUrl] });
        setIsRefreshingList(false);
      });
  }, [dropServerCache, queryClient, apiUrl, t]);

  const handleRefreshList = useCallback(() => {
    // Only Nedarim has a server-side copy to drop; the accounting list is read fresh each time.
    if (!apiUrl) { queryClient.invalidateQueries({ queryKey: [debitsUrl] }); return; }
    dropServerCache({ force: true })
      .catch(() => undefined)
      .finally(() => { queryClient.invalidateQueries({ queryKey: [apiUrl] }); });
  }, [dropServerCache, queryClient, apiUrl, debitsUrl]);

  /*
   * The pill in the second slot: on the credit board a card about to run out, on the bank board an
   * order about to run out of payments. Same place, same colour, the bank's version of the question.
   */
  const needsSecondPill = useCallback((order: NedarimStandingOrderDtoIn): boolean => (
    isBankBoard ? isAptEndingSoon(order) : cardNeedsAttention(order)
  ), [isBankBoard]);

  // A search looks through every order, frozen and finished ones too (isOutOfView).
  const searching = searchQuery.trim() !== '';

  const hiddenInactiveCount = useMemo(
    () => (isBankBoard ? 0 : (visibleStandingOrders ?? []).filter(isInactive).length),
    [isBankBoard, visibleStandingOrders]);

  // The filter row, read the same way on the by-donor screen (boardColumns.filterBoardRows).
  const board = useMemo(() => ({ isBank: isBankBoard, needsSecondPill }), [isBankBoard, needsSecondPill]);
  const filteredStandingOrders = useMemo(
    () => (visibleStandingOrders ? filterBoardRows(visibleStandingOrders, { attention, dateRange, searching }, board) : []),
    [visibleStandingOrders, dateRange, attention, board, searching]);

  const failureSummary = useMemo(() => summarizeFailures(visibleStandingOrders), [visibleStandingOrders]);

  const attentionCounts = useMemo(() => countAttention(visibleStandingOrders, board), [visibleStandingOrders, board]);

  /*
   * Deliberately not memoised. Col() and Cell() call useTranslation() inside, so wrapping this
   * in useMemo makes those hooks conditional and React loses its hook order on the next render.
   * The audit asked for the memo (finding A11); it needs the translation lifted out of the two
   * shared helpers first, which touches every board that uses them.
   */
  const columnContext = { t, locale: i18n.language, frozenTerm };
  const columns: ColumnDef<NedarimStandingOrderDtoIn>[] = [
    donorColumn(columnContext),
    ...orderColumns(columnContext),
    {
      /*
       * One column for whatever this row lets you do. A row that failed offers the charge
       * again; every other row offers to open an inquiry about it. Two columns meant a gap
       * beside every button and a button-shaped hole beside every icon, and the width came
       * out of the numbers.
       */
      id: 'actions',
      header: () => <span className="sr-only">{t('standingOrders.actions')}</span>,
      cell: ({ row }) => {
        const own = renderRowOwnAction(row.original);
        // HAT-1317: the order's own actions, behind three dots, beside whatever the row offers -
        // on Nedarim's credit orders, and (HAT-1441) on the bank board's orders, changed in APT.
        // The office's own credit orders on the credit board are changed by the office.
        const withMenu = isAptOrder(row.original)
          ? hasBankActions && row.original.type === STANDING_ORDER_TYPE_BANK
          : hasOrderActions && row.original.type === STANDING_ORDER_TYPE_CREDIT;
        if (!withMenu) return own;
        // The bank actions' announcement (HAT-1489) points at these dots, on the first row in view -
        // or, while an order is open, at the same actions in its blade, which sits over this column.
        const menu = <CustomMenu items={rowMenuItems(row.original)} />;
        return (
          <span className={styles.rowActions}>
            {own}
            <span className={styles.menuSlot} aria-label={t('standingOrders.orderActions.menuLabel')}>
              {isAptOrder(row.original) ? <AnnouncementAnchor name={selectedOrder ? '' : BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY}>{menu}</AnnouncementAnchor> : menu}
            </span>
          </span>
        );
      },
      meta: {
        className: (isBankBoard ? hasBankActions : hasOrderActions) ? 'min-w-[128px] w-[128px]' : 'min-w-[88px] w-[88px]',
        lockLayout: true,
      },
    },
  ];

  // Everything the row holds, not only what fits on screen (boardColumns.buildExportColumns).
  const exportColumns: ColumnDef<NedarimStandingOrderDtoIn>[] = useMemo(
    () => buildExportColumns({ t, locale: i18n.language, frozenTerm }, isBankBoard),
    [t, i18n.language, isBankBoard, frozenTerm]);

  /**
   * One message used to cover three different situations, and two of them were untrue: a fund
   * that had never set up a standing order, a fund whose orders the filters were hiding, and a
   * fetch that had failed all read "there are no standing orders".
   */
  const emptyStateContent = useMemo(() => {
    if (isError) {
      return {
        title: t(isBankBoard ? 'standingOrders.loadFailedBank' : 'standingOrders.loadFailed'),
        description: t(isBankBoard ? 'standingOrders.loadFailedBankDescription' : 'standingOrders.loadFailedDescription'),
        action: <Button onClick={() => refetch()}>{t('retry')}</Button>,
      };
    }

    // Every order of the fund is finished, frozen or cancelled, so the default view is empty: say why.
    if (!dateRange && attention.size === 0 && !searching && !isBankBoard && hiddenInactiveCount > 0 && (visibleStandingOrders?.length ?? 0) === hiddenInactiveCount) {
      return {
        title: t('standingOrders.noActiveOrders'),
        description: t('standingOrders.noActiveOrdersDescription', { total: hiddenInactiveCount }),
        action: (
          <Button variant="outline" onClick={() => setAttention(new Set<AttentionFilter>(['frozen', 'ended']))}>
            {t('standingOrders.showInactive')}
          </Button>
        ),
      };
    }

    const hasFilters = Boolean(dateRange) || attention.size > 0 || Boolean(searchQuery);

    if (hasFilters && (visibleStandingOrders?.length ?? 0) > 0) {
      return {
        title: t('standingOrders.noResults'),
        description: t('standingOrders.noResultsDescription', {
          total: visibleStandingOrders?.length ?? 0,
        }),
        action: (
          <Button variant="outline" onClick={handleClearFilters}>{t('clearFilters')}</Button>
        ),
      };
    }

    return {
      title: t(isBankBoard ? 'standingOrders.noBankData' : 'standingOrders.noData'),
      description: t(isBankBoard ? 'standingOrders.noBankDataDescription' : 'standingOrders.noDataDescription'),
    };
  }, [
    isError, refetch, t, dateRange, attention, hiddenInactiveCount, searching,
    searchQuery, visibleStandingOrders, handleClearFilters, isBankBoard,
  ]);

  const boardTitle = t(isBankBoard ? 'standingOrders.bankTitle' : 'standingOrders.title');

  return (
    <div className={styles.page}>
      {currentFund ? (
        <>
          {/* Board navigation on a narrow screen (HAT-1170); renders nothing above 1100. */}
          <BoardTabs />
          <DataTable
            /*
             * The page scrolls, not a box inside it - the same as the transactions board beside
             * it in the tab row. A board of its own height meant the table had a fixed window
             * with its own scrollbar while the page had another, and the chrome above it stayed
             * put however far down the rows you were. With document scrolling the heading,
             * filters and column titles behave as one sheet, and the pin keeps the controls in
             * view when they are wanted.
             */
            scrollMode="document"
            /*
             * Narrow screen (HAT-1170): cards instead of rows, the board's own name carried by
             * the active tab, and the primary action as a card above the list. Each board's card
             * opens that board's own creation form.
             */
            hideTitleOnCards={hasBoardTabs}
            cardLayout="flat"
            cardChromeTop={canCreate ? (
              <AnnouncementAnchor name={createAnchor}>
                <FabActionCard label={t('standingOrders.newOrder.addStandingOrder')} onClick={() => setCreationOpen(true)} />
              </AnnouncementAnchor>
            ) : undefined}
            renderCard={(row) => (
              <StandingOrderCard
                row={row}
                isActive={row.original === selectedOrder}
                onOpen={handleRowClick}
                hasInquiries={hasInquiries}
                lastChargeColors={LAST_CHARGE_COLORS}
                activeColors={ACTIVE_COLORS}
                frozenTerm={frozenTerm}
              />
            )}
            // The old site opened sorted by the last action. Ours opened unsorted, in whatever
            // order Nedarim happened to answer, so the rows that need a coordinator could be
            // anywhere in the list.
            sortBy={{ id: 'lastCharge', desc: true }}
            title={boardTitle}
            data={filteredStandingOrders}
            isLoading={isLoading}
            /*
             * Drook, on the filter row: "very, very crowded here - on the transactions board it
             * is more right." It held the chips, density, search, the refresh control and the
             * new-order button on one line. The two that are about the board rather than about
             * narrowing it move up beside the title and export, where the transactions board
             * keeps its own. On a narrow screen the action is the card above the list instead.
             */
            headerActions={isMobile || !canCreate ? undefined : (
              <>
                {/* The refresh is Nedarim's (HAT-1382); the bank list is APT's copy and has none. */}
                {!isBankBoard && refreshNote && <span className={styles.refreshNote} role="status">{refreshNote}</span>}
                {!isBankBoard && (
                  <Button className={styles.addButton} onClick={handleManualRefresh} disabled={isRefreshingList}>
                    {t('standingOrders.refreshList')}
                  </Button>
                )}
                <AnnouncementAnchor name={createAnchor}>
                  <Button className={styles.addButton} onClick={() => setCreationOpen(true)}>
                    {t('standingOrders.newOrder.addStandingOrder')}
                  </Button>
                </AnnouncementAnchor>
              </>
            )}
            columns={columns}
            exportColumns={exportColumns}
            exportFilename={`${boardTitle} - ${currentFund.name}`}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            density={density}
            onDensityChange={setDensity}
            /*
             * What the board adds up to, under its own heading. It was a row of three cards
             * and then a line in the notice band, where 24px of text sat inside 68px of air;
             * a caption belongs to the title it describes.
             */
            titleCaption={
              <StandingOrdersSummary
                data={filteredStandingOrders}
                totalCount={visibleStandingOrders?.length ?? 0}
                isLoading={isLoading}
              />
            }
            notice={
              /* Its own full-width row under the board's heading: the filters slot shares its
                 row with the toolbar, so a banner put there only gets part of the width, and
                 HAT-1238 needs it min-w-0 so Fold 280 does not grow from its min-content. */
              failureSummary.failed > 0 && !isError && !bannerDismissed ? (
                <div className="min-w-0 w-full max-w-full">
                  {/* HAT-1239 anatomy: title = count of failures, body = the card subset when
                      relevant, CTA = filter to failed. One line rather than three: the whole
                      thing is one sentence, and stacked it made a tall box around very little. */}
                  <Alert
                    type="error"
                    inline
                    title={t(isBankBoard ? 'standingOrders.returnBanner' : 'standingOrders.failureBanner', { count: failureSummary.failed })}
                    body={!isBankBoard && failureSummary.expiringOrExpired > 0
                      ? t('standingOrders.failureBannerCards', { count: failureSummary.expiringOrExpired })
                      : undefined}
                    action={{
                      label: t(isBankBoard ? 'standingOrders.showReturned' : 'standingOrders.showFailed'),
                      /*
                       * And then it goes. The banner asks to be acted on; once the board is
                       * filtered to exactly what it was pointing at, it is a second copy of
                       * the "נכשל" pill sitting above the answer. Dismissing is the same
                       * dismissal the X performs - for this visit only, so the banner is back
                       * on the next load if the failures still are.
                       */
                      onClick: () => {
                        setAttention(new Set(['failed']));
                        setBannerDismissed(true);
                      },
                    }}
                    onClose={() => setBannerDismissed(true)}
                    className={styles.failureBannerSpacing}
                  />
                </div>
              ) : undefined
            }
            columnLayoutKey={isBankBoard ? 'bankStandingOrders' : 'standingOrders'}
            twoLineRows
            searchPlaceholder="standingOrders.searchStandingOrder"
            /* What the search actually covers: too long for a 220px field, which cut it mid-word. */
            searchTooltip={isBankBoard ? 'standingOrders.searchBankHint' : 'standingOrders.searchHint'}
            customGlobalSearch={standingOrderSearch}
            emptyStateContent={emptyStateContent}
            handleRowClick={handleRowClick}
            activeRecord={selectedOrder}
            filters={
              <div className="flex flex-col gap-5">
                <FiltersArea
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  attention={attention}
                  onToggleAttention={toggleAttention}
                  attentionCounts={attentionCounts}
                  attentionLabelKeys={{
                    ...(isBankBoard
                      ? { failed: 'standingOrders.attention_returned', expiring: 'standingOrders.attention_endingSoon' }
                      : {}),
                    // The row's own word: «מוקפאת» where the board can freeze, «לא פעילה» where it cannot.
                    frozen: frozenTerm ? 'standingOrders.attention_frozen' : 'standingOrders.attention_disabled',
                  }}
                  isLoading={isLoading}
                  onClearFilters={handleClearFilters}
                />
              </div>
            }
          />
          {selectedOrder && currentFund && (
            <AdditionalInfo
              key={selectedOrder.standingOrderId}
              data={selectedOrder}
              fundId={currentFund.id}
              handleXclick={handleCloseBlade}
              onCharge={setChargeTarget}
              hasOrderActions={hasOrderActions}
              hasBankActions={hasBankActions}
              collectionLock={lockOf(selectedOrder)}
              onAction={(kind) => setActionTarget({ kind, order: selectedOrder })}
              notice={actionNotice?.orderId === selectedOrder.standingOrderId ? actionNotice : null}
              onDismissNotice={() => setActionNotice(null)}
            />
          )}
          {actionTarget && (actionTarget.kind === 'freeze' || actionTarget.kind === 'reactivate') && (
            <ToggleOrderDialog
              key={`${actionTarget.kind}-${actionTarget.order.standingOrderId}`}
              kind={actionTarget.kind}
              order={actionTarget.order}
              fundId={currentFund.id}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result) => {
                handleActionDone(actionTarget.order, kind, result);
                setActionTarget(undefined);
              }}
            />
          )}
          {actionTarget?.kind === 'cancel' && (
            <CancelOrderDialog
              key={`cancel-${actionTarget.order.standingOrderId}`}
              order={actionTarget.order}
              state={isAptOrder(actionTarget.order)
                ? toOrderState(getBankOrderState(actionTarget.order)) ?? 'ended'
                : getOrderState(actionTarget.order)}
              fundId={currentFund.id}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result) => handleActionDone(actionTarget.order, kind, result)}
            />
          )}
          {actionTarget?.kind === 'edit' && isAptOrder(actionTarget.order) && (
            <EditBankOrderDialog
              key={`edit-${actionTarget.order.standingOrderId}`}
              order={actionTarget.order}
              fundId={currentFund.id}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result, request) => handleActionDone(actionTarget.order, kind, result, request)}
            />
          )}
          {actionTarget?.kind === 'edit' && !isAptOrder(actionTarget.order) && (
            <EditOrderDialog
              key={`edit-${actionTarget.order.standingOrderId}`}
              order={actionTarget.order}
              fundId={currentFund.id}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result, request) => handleActionDone(actionTarget.order, kind, result, request)}
            />
          )}
          {chargeTarget && (
            <StandingOrderChargeDialog
              // Keyed by order so an amount never carries over from the last donor charged.
              key={chargeTarget.standingOrderId}
              open
              onOpenChange={(isOpen) => { if (!isOpen) setChargeTarget(undefined); }}
              type={chargeTarget.type === STANDING_ORDER_TYPE_BANK ? 'bank' : 'credit'}
              fundId={currentFund.id}
              standingOrderId={chargeTarget.standingOrderId}
              mosadSource={chargeTarget.mosadSource}
              defaultAmount={chargeTarget.amount}
              currency={chargeTarget.currency}
              donorName={chargeTarget.clientName}
              paymentSource={getPaymentSourceLabel(chargeTarget)}
              validity={chargeTarget.validity}
              onChargeSucceeded={handleRefreshList}
            />
          )}
          {creationOpen && (
            <NewStandingOrderDialog
              fundId={currentFund.id}
              variant={variant}
              onClose={() => setCreationOpen(false)}
              onRefreshList={handleRefreshList}
            />
          )}
        </>
      ) : (
        <h1>{t('noFundSelected')}.</h1>
      )}
    </div>
  );
};

export default StandingOrdersPage;
